/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import agentAwareUtils from 'utils/AgentAware';

import { Flex, Button } from 'components/ui';
import CheckoutButton from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/CheckoutButton';
import PayPalButton from 'components/PayPalButton/PayPalButton';
import PayPal from 'utils/PayPal';
import PayPalCheckout from 'utils/PayPalCheckout';
import ApplePayButton from 'components/ApplePayButton/ApplePayButton';
import VenmoExpressButton from 'components/VenmoExpressButton/VenmoExpressButton';

import {
    mediaQueries, lineHeights, colors, space
} from 'style/config';

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column', // on large screens
        gap: space[3],
        [mediaQueries.xsMax]: {
            flexDirection: 'row', // on small screens
            flexWrap: 'wrap',
            justifyContent: 'space-evenly',
            width: '100%'
        }
    },
    button: {
        [mediaQueries.xsMax]: {
            display: 'flex',
            flex: 1
        }
    },
    checkoutButton: {
        lineHeight: lineHeights.tight,
        [mediaQueries.xsMax]: {
            display: 'flex',
            width: '100%',
            order: 0
        }
    },
    checkoutButtonWithOrder: {
        lineHeight: lineHeights.tight,
        order: -1,
        [mediaQueries.xsMax]: {
            display: 'flex',
            flex: 1,
            order: 1
        }
    },
    paypalLoadingButtonBase: {
        width: '100%',
        backgroundColor: colors.paypalYellow,
        transition: 'opacity 0.3s ease-in-out',
        ':disabled': {
            backgroundColor: colors.paypalYellow,
            borderColor: colors.paypalYellow,
            color: colors.white,
            cursor: 'not-allowed'
        },
        '& > div': {
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTop: `2px solid ${colors.white}`
        }
    },
    paypalContainerBase: {
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        transition: 'opacity 0.3s ease-in-out',
        // PayPal creates its own button and a div wrapper around
        // the iframe. This div has a min-width of 150px, which makes it
        // overlap other buttons in small screens
        '& > div': {
            minWidth: '0 !important'
        }
    },
    visible: {
        opacity: 1,
        pointerEvents: 'auto'
    },
    hidden: {
        opacity: 0,
        pointerEvents: 'none'
    },
    loadingButton: {
        position: 'relative',
        [mediaQueries.xsMax]: {
            display: 'flex',
            flex: 1
        }
    }
};

class ButtonGrid extends BaseClass {
    static counter = 0;

    constructor(props) {
        super(props);

        this.state = {
            isApplePayPayment: false,
            isPayPalLoading: true
        };
        // One button grid is rendered for desktop and another for mobile,
        // so we have to generate a unique container ID for PayPal.Buttons
        const counterId = ButtonGrid.counter++;
        this.paypalContainerId = `paypal-checkout-container-${String(counterId).padStart(2, '0')}`;
    }

    async componentDidMount() {
        this.isComponentMounted = true;

        this.props.getApplePaymentStatus().then(isApplePayPayment => this.setState({ isApplePayPayment }));

        // Don't render PayPal button if hideCheckoutButton is true (component returns null)
        if (this.props.hideCheckoutButton) {
            return;
        }

        if (PayPal.isPayPalCheckoutEnabledForLocale() && this.props.isPaypalPayment) {
            await this.renderPayPalCheckoutButton();
        }
    }

    componentWillUnmount() {
        this.isComponentMounted = false;
    }

    renderPayPalCheckoutButton = async () => {
        const { isBopis } = this.props;

        try {
            await PayPalCheckout.preparePaypalCheckout();
            await PayPalCheckout.renderPayPalButton({ containerId: this.paypalContainerId, isBopis });

            if (this.isComponentMounted) {
                this.setState({ isPayPalLoading: false });
            }
        } catch (error) {
            Sephora.logger.error('[PayPalCheckout] Error rendering PayPal button:', error);

            if (this.isComponentMounted) {
                this.setState({ isPayPalLoading: false });
            }
        }
    };

    componentDidUpdate() {
        this.props.getApplePaymentStatus().then(isApplePayPayment => {
            if (isApplePayPayment !== this.state.isApplePayPayment) {
                this.setState({ isApplePayPayment });
            }
        });
    }

    render() {
        const {
            isBopis,
            isPaypalPayment,
            isVenmoEligible,
            isCheckoutDisabled,
            isSignedIn,
            isSDDBasketAvailable,
            isSDUOnlyInBasket,
            isRougeRewardsApplied,
            hideCheckoutButton
        } = this.props;

        const { isApplePayPayment } = this.state;
        const hasSDDItem = isSDDBasketAvailable && !isSDUOnlyInBasket;

        const {
            container,
            button,
            checkoutButton,
            checkoutButtonWithOrder,
            paypalLoadingButtonBase,
            paypalContainerBase,
            visible,
            hidden,
            loadingButton
        } = styles;

        const updateCheckoutButtonStyle = count => {
            return count === 1 ? checkoutButtonWithOrder : checkoutButton;
        };

        if (hideCheckoutButton) {
            return null;
        }

        const buttonCount = [isPaypalPayment, isVenmoEligible, isApplePayPayment].filter(Boolean).length;
        const payPalCheckoutEnabled = PayPal.isPayPalCheckoutEnabledForLocale();

        return (
            <Flex css={container}>
                <CheckoutButton
                    isBopis={isBopis}
                    isCheckoutDisabled={isCheckoutDisabled}
                    isSignedIn={isSignedIn}
                    hasSDDItem={hasSDDItem}
                    isRougeRewardsApplied={isRougeRewardsApplied}
                    css={updateCheckoutButtonStyle(buttonCount)}
                />
                {isApplePayPayment && (
                    <ApplePayButton
                        isBopis={isBopis}
                        isApplePayPayment={isApplePayPayment}
                        iconOnly
                        css={button}
                    />
                )}
                {payPalCheckoutEnabled && isPaypalPayment && (
                    <Flex css={loadingButton}>
                        <Button
                            variant='primary'
                            loading={true}
                            disabled={true}
                            tabIndex={-1}
                            aria-busy={this.state.isPayPalLoading}
                            aria-label='Loading PayPal'
                            aria-hidden={!this.state.isPayPalLoading}
                            css={[paypalLoadingButtonBase, this.state.isPayPalLoading ? visible : hidden]}
                        />
                        <div
                            id={this.paypalContainerId}
                            aria-hidden={this.state.isPayPalLoading}
                            css={[paypalContainerBase, this.state.isPayPalLoading ? hidden : visible]}
                        />
                    </Flex>
                )}
                {!payPalCheckoutEnabled && isPaypalPayment && (
                    <PayPalButton
                        className={agentAwareUtils.applyHideAgentAwareClass()}
                        isBopis={isBopis}
                        isPaypalPayment={isPaypalPayment}
                        css={button}
                    />
                )}
                {isVenmoEligible && (
                    <VenmoExpressButton
                        className={agentAwareUtils.applyHideAgentAwareClass()}
                        isBopis={isBopis}
                        isVenmoEligible={isVenmoEligible}
                        css={button}
                    />
                )}
            </Flex>
        );
    }
}

export default wrapComponent(ButtonGrid, 'ButtonGrid', true);
